import React from 'react';
import { Box, Typography } from '@mui/material';
import { WidgetCard, WidgetEmptyState } from './WidgetCard';
import { buildHeatmap } from './widgetData';

const getColor = (value, max) => {
  if (!max) return 'rgba(25, 118, 210, 0.15)';
  const intensity = Math.min(0.9, 0.15 + (value / max) * 0.75);
  return `rgba(25, 118, 210, ${intensity})`;
};

const HeatmapWidget = ({ widget, rows }) => {
  const { title, xField, yField, valueField, maxBuckets, showLegend } = widget.params || {};
  const { xLabels, yLabels, matrix } = buildHeatmap({ rows, xField, yField, valueField, maxBuckets });

  if (!xLabels.length || !yLabels.length) {
    return (
      <WidgetCard
        title={title || 'Heatmap'}
        subtitle={`${xField} x ${yField}`}
        testId={`report-widget-${widget.id || widget.type}`}
      >
        <WidgetEmptyState message="Heatmap data is not available yet." />
      </WidgetCard>
    );
  }

  const maxValue = Math.max(...matrix.flat());
  const legendGradient = `linear-gradient(90deg, ${getColor(0, maxValue)}, ${getColor(maxValue, maxValue)})`;

  return (
    <WidgetCard
      title={title || 'Heatmap'}
      subtitle={`${xField} x ${yField}`}
      testId={`report-widget-${widget.id || widget.type}`}
    >
      <Box sx={{ display: 'grid', gridTemplateColumns: `140px repeat(${xLabels.length}, minmax(24px, 1fr))`, gap: 0.5 }}>
        <Box />
        {xLabels.map((label) => (
          <Typography key={label} variant="caption" sx={{ textAlign: 'center' }}>
            {label}
          </Typography>
        ))}
        {yLabels.map((yLabel, rowIndex) => (
          <React.Fragment key={yLabel}>
            <Typography variant="caption" sx={{ pr: 1 }}>{yLabel}</Typography>
            {xLabels.map((xLabel, colIndex) => {
              const value = matrix[rowIndex][colIndex] || 0;
              return (
                <Box
                  key={`${yLabel}-${xLabel}`}
                  sx={{
                    height: 20,
                    borderRadius: 0.5,
                    backgroundColor: getColor(value, maxValue),
                  }}
                  title={`${xLabel} / ${yLabel}: ${value}`}
                />
              );
            })}
          </React.Fragment>
        ))}
      </Box>
      {showLegend && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
          <Typography variant="caption" color="text.secondary">0</Typography>
          <Box sx={{ flex: 1, height: 8, borderRadius: 999, background: legendGradient }} />
          <Typography variant="caption" color="text.secondary">{maxValue || 0}</Typography>
        </Box>
      )}
    </WidgetCard>
  );
};

export default HeatmapWidget;
